import { ArrowBackIcon } from "@chakra-ui/icons";
import { IconButton, Button, VStack } from "@chakra-ui/react";
import { SylaApiError } from "@syla/shared/errors/SylaApiError";
import { DataSourceWithNetworks } from "@syla/shared/types/responses/DataSourceWithNetworks";
import React from "react";
import { useFormContext } from "react-hook-form";
import { DataSourceInputType } from "../../../../helper/dataSource/DataSourceInputType";
import { AdvancedOptions } from "../components/AdvancedOptions";
import { DataSourceDrawerFooter } from "../components/DataSourceDrawerFooter";
import { DataSourceDrawHeader } from "../components/DataSourceDrawerHeader";
import { DataSourceTitle } from "../components/DataSourceTitle";
import { ImportErrorAlert } from "../components/ImportErrorAlert";
import { ImportMethodHeading } from "../components/ImportMethodHeading";
import { ImportMethodInputsSection } from "../components/ImportMethodInputsSection";
import { InstructionsSection } from "../components/InstructionsSection";
import { AddDataSourceForm } from "../workflows/AddDataSourceWorkflow";
import { ImportMethod } from "./ImportMethodSelectionPanel";

export function CompletingInputPanel({
  dataSource,
  importMethod,
  onBack,
  error,
  setError,
  onImportStart,
}: {
  dataSource: DataSourceWithNetworks;
  importMethod: ImportMethod;
  onBack: () => void;
  error: Error | SylaApiError | unknown;
  setError: (error: Error | SylaApiError | unknown) => void;
  onImportStart: (
    values: AddDataSourceForm,
    walletId: string | undefined,
  ) => Promise<void>;
}): React.ReactElement {
  const form = useFormContext<AddDataSourceForm>();

  return (
    <>
      <DataSourceDrawHeader>
        <IconButton
          variant="ghost"
          aria-label="Back"
          icon={<ArrowBackIcon />}
          boxSize="2.5rem"
          fontSize="2xl"
          onClick={onBack}
        />
        <DataSourceTitle
          name={form.watch("customName")}
          image={dataSource.image}
        />
      </DataSourceDrawHeader>

      <VStack
        w="full"
        px={4}
        py={4}
        overflowY="auto"
        flex={1}
        align="flex-start"
        spacing={0}
        gap={8}
      >
        <ImportErrorAlert
          error={error}
          isCustomDataSource={importMethod === DataSourceInputType.Custom}
          onClose={() => setError(undefined)}
        />

        <InstructionsSection
          w="full"
          dataSource={dataSource}
          importMethod={importMethod}
          isNewDataSource={true}
        />

        <VStack
          w="full"
          align="flex-start"
          spacing={6}
          id="add-data-source-form"
          as="form"
          onSubmit={form.handleSubmit(async (values) => {
            await onImportStart(values, undefined);
          })}
          noValidate
        >
          <ImportMethodHeading dataSource={dataSource} method={importMethod} />
          <ImportMethodInputsSection
            dataSource={dataSource}
            importMethod={importMethod}
            onAuthorised={async (walletId) => {
              await form.handleSubmit(async (values) => {
                await onImportStart(values, walletId);
              })();
            }}
            onImportError={setError}
          />
          <AdvancedOptions
            dataSource={dataSource}
            importMethod={importMethod}
          />
        </VStack>
      </VStack>

      <DataSourceDrawerFooter>
        <Button variant="outline" colorScheme="gray" onClick={onBack}>
          Back
        </Button>
        <Button
          form="add-data-source-form"
          variant="solid"
          colorScheme="primary"
          disabled={
            !canImportProceed(
              dataSource,
              importMethod,
              form.watch("storedFiles")?.length ?? 0,
            )
          }
          type="submit"
        >
          {proceedButtonText(importMethod)}
        </Button>
      </DataSourceDrawerFooter>
    </>
  );
}

function proceedButtonText(importMethod?: ImportMethod) {
  if (importMethod === DataSourceInputType.Api) {
    return "Sync";
  }

  if (importMethod === "Empty") {
    return "Create";
  }

  return "Import";
}

function canImportProceed(
  dataSource: DataSourceWithNetworks,
  importMethod: ImportMethod,
  storedFilesCount: number,
) {
  if (importMethod === "Empty") {
    return true;
  }

  if (importMethod === DataSourceInputType.Api) {
    // in the OAuth button, we never enable the sync button, it will instead
    // trigger automatically when the user completes the OAuth flow.
    if (dataSource.syncAuthType === "oauth") {
      return false;
    }

    return true;
  }

  if (storedFilesCount > 0) {
    return true;
  }

  return false;
}
